import _slicedToArray from "/drone/src/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _defineProperty from "/drone/src/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _classCallCheck from "/drone/src/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/drone/src/node_modules/@babel/runtime/helpers/esm/createClass.js";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { getCurrentHub } from '@sentry/core';
import { addInstrumentationHandler, isString, isPrimitive, isErrorEvent, getLocationHref, logger, addExceptionMechanism } from '@sentry/utils';
import { eventFromUnknownInput } from '../eventbuilder.js';
import { shouldIgnoreOnError } from '../helpers.js';

/* eslint-disable @typescript-eslint/no-unsafe-member-access */

/** Global handlers */
var GlobalHandlers = /*#__PURE__*/function () {
  /** JSDoc */
  function GlobalHandlers(options) {
    _classCallCheck(this, GlobalHandlers);
    ;
    GlobalHandlers.prototype.__init.call(this);
    GlobalHandlers.prototype.__init2.call(this);
    this._options = _objectSpread({
      onerror: true,
      onunhandledrejection: true
    }, options);
  }
  /**
   * @inheritDoc
   */
  _createClass(GlobalHandlers, [{
    key: "__init",
    value:
    /**
     * @inheritDoc
     */
    function __init() {
      this.name = GlobalHandlers.id;
    }

    /** JSDoc */

    /**
     * Stores references functions to installing handlers. Will set to undefined
     * after they have been run so that they are not used twice.
     */
  }, {
    key: "__init2",
    value: function __init2() {
      this._installFunc = {
        onerror: _installGlobalOnErrorHandler,
        onunhandledrejection: _installGlobalOnUnhandledRejectionHandler
      };
    }
  }, {
    key: "setupOnce",
    value: function setupOnce() {
      Error.stackTraceLimit = 50;
      var options = this._options;

      // We can disable guard-for-in as we construct the options object above + do checks against
      // `this._installFunc` for the property.
      // eslint-disable-next-line guard-for-in
      for (var key in options) {
        var installFunc = this._installFunc[key];
        if (installFunc && options[key]) {
          globalHandlerLog(key);
          installFunc();
          this._installFunc[key] = undefined;
        }
      }
    }
  }], [{
    key: "__initStatic",
    value:
    /**
     * @inheritDoc
     */
    function __initStatic() {
      this.id = 'GlobalHandlers';
    }
  }]);
  return GlobalHandlers;
}();
GlobalHandlers.__initStatic();

/** JSDoc */
function _installGlobalOnErrorHandler() {
  addInstrumentationHandler('error',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function (data) {
    var _getHubAndOptions = getHubAndOptions(),
      _getHubAndOptions2 = _slicedToArray(_getHubAndOptions, 3),
      hub = _getHubAndOptions2[0],
      stackParser = _getHubAndOptions2[1],
      attachStacktrace = _getHubAndOptions2[2];
    if (!hub.getIntegration(GlobalHandlers)) {
      return;
    }
    var msg = data.msg,
      url = data.url,
      line = data.line,
      column = data.column,
      error = data.error;
    if (shouldIgnoreOnError() || error && error.__sentry_own_request__) {
      return;
    }
    var event = error === undefined && isString(msg) ? _eventFromIncompleteOnError(msg, url, line, column) : _enhanceEventWithInitialFrame(eventFromUnknownInput(stackParser, error || msg, undefined, attachStacktrace, false), url, line, column);
    event.level = 'error';
    addMechanismAndCapture(hub, error, event, 'onerror');
  });
}

/** JSDoc */
function _installGlobalOnUnhandledRejectionHandler() {
  addInstrumentationHandler('unhandledrejection',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function (e) {
    var _getHubAndOptions3 = getHubAndOptions(),
      _getHubAndOptions4 = _slicedToArray(_getHubAndOptions3, 3),
      hub = _getHubAndOptions4[0],
      stackParser = _getHubAndOptions4[1],
      attachStacktrace = _getHubAndOptions4[2];
    if (!hub.getIntegration(GlobalHandlers)) {
      return;
    }
    var error = e;

    // dig the object of the rejection out of known event types
    try {
      // PromiseRejectionEvents store the object of the rejection under 'reason'
      // see https://developer.mozilla.org/en-US/docs/Web/API/PromiseRejectionEvent
      if ('reason' in e) {
        error = e.reason;
      }
      // something, somewhere, (likely a browser extension) effectively casts PromiseRejectionEvents
      // to CustomEvents, moving the `promise` and `reason` attributes of the PRE into
      // the CustomEvent's `detail` attribute, since they're not part of CustomEvent's spec
      // see https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent and
      // https://github.com/getsentry/sentry-javascript/issues/2380
      else if ('detail' in e && 'reason' in e.detail) {
        error = e.detail.reason;
      }
    } catch (_oO) {
      // no-empty
    }
    if (shouldIgnoreOnError() || error && error.__sentry_own_request__) {
      return true;
    }
    var event = isPrimitive(error) ? _eventFromRejectionWithPrimitive(error) : eventFromUnknownInput(stackParser, error, undefined, attachStacktrace, true);
    event.level = 'error';
    addMechanismAndCapture(hub, error, event, 'onunhandledrejection');
    return;
  });
}

/**
 * Create an event from a promise rejection where the `reason` is a primitive.
 *
 * @param reason: The `reason` property of the promise rejection
 * @returns An Event object with an appropriate `exception` value
 */
function _eventFromRejectionWithPrimitive(reason) {
  return {
    exception: {
      values: [{
        type: 'UnhandledRejection',
        // String() is needed because the Primitive type includes symbols (which can't be automatically stringified)
        value: "Non-Error promise rejection captured with value: ".concat(String(reason))
      }]
    }
  };
}

/**
 * This function creates a stack from an old, error-less onerror handler.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function _eventFromIncompleteOnError(msg, url, line, column) {
  var ERROR_TYPES_RE = /^(?:[Uu]ncaught (?:exception: )?)?(?:((?:Eval|Internal|Range|Reference|Syntax|Type|URI|)Error): )?(.*)$/i;

  // If 'message' is ErrorEvent, get real message from inside
  var message = isErrorEvent(msg) ? msg.message : msg;
  var name = 'Error';
  var groups = message.match(ERROR_TYPES_RE);
  if (groups) {
    name = groups[1];
    message = groups[2];
  }
  var event = {
    exception: {
      values: [{
        type: name,
        value: message
      }]
    }
  };
  return _enhanceEventWithInitialFrame(event, url, line, column);
}

/** JSDoc */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function _enhanceEventWithInitialFrame(event, url, line, column) {
  // event.exception
  var e = event.exception = event.exception || {};
  // event.exception.values
  var ev = e.values = e.values || [];
  // event.exception.values[0]
  var ev0 = ev[0] = ev[0] || {};
  // event.exception.values[0].stacktrace
  var ev0s = ev0.stacktrace = ev0.stacktrace || {};
  // event.exception.values[0].stacktrace.frames
  var ev0sf = ev0s.frames = ev0s.frames || [];
  var colno = isNaN(parseInt(column, 10)) ? undefined : column;
  var lineno = isNaN(parseInt(line, 10)) ? undefined : line;
  var filename = isString(url) && url.length > 0 ? url : getLocationHref();

  // event.exception.values[0].stacktrace.frames
  if (ev0sf.length === 0) {
    ev0sf.push({
      colno: colno,
      filename: filename,
      function: '?',
      in_app: true,
      lineno: lineno
    });
  }
  return event;
}
function globalHandlerLog(type) {
  (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log("Global Handler attached: ".concat(type));
}
function addMechanismAndCapture(hub, error, event, type) {
  addExceptionMechanism(event, {
    handled: false,
    type: type
  });
  hub.captureEvent(event, {
    originalException: error
  });
}
function getHubAndOptions() {
  var hub = getCurrentHub();
  var client = hub.getClient();
  var options = client && client.getOptions() || {
    stackParser: function stackParser() {
      return [];
    },
    attachStacktrace: false
  };
  return [hub, options.stackParser, options.attachStacktrace];
}
export { GlobalHandlers };